import { faBuilding, faCalendar, faMapMarker, faSuitcase } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Loader from '../components/Loader'
import { getUserAppoinments, removeUserAppointment } from '../resources/api-constants'
import { ROUTES } from '../resources/other-constants'
import { Customer, UserAppointment } from '../types/services'
import CustomAxios from '../utility/customAxios'

const AppointmentDetailPage: React.FC = () => {
    const [userAppointments, setUserAppointments] = useState<UserAppointment[][]>([])
    const [userInfo, setUserInfo] = useState<Customer | null>(null)
    const [loading, setLoading] = useState(false)
    const userToken = new URLSearchParams(document.location.search).get('userToken')

    /**
     * Ad ogni aggiornamento dei dati degli appuntamenti ricava i dati dell'utente corrente
     */
    useEffect(() => {
        if (userAppointments && userAppointments.length > 0) {
            const userData = userAppointments[0][0].customer
            if (userData) setUserInfo(userData)
        }
    }, [userAppointments])

    /**
     * Carica i dati degli appuntamenti in arrivo dell'utente corrente
     */
    const loadUserAppointmentsData = useCallback(async () => {
        try {
            setUserAppointments([])
            if (!userToken) return
            const today = moment(new Date()).format('YYYY-MM-DD')
            const appointmentsData: UserAppointment[] = await CustomAxios.get(`${getUserAppoinments()}&date_start=${today}`, {
                headers: {
                    Authorization: `Bearer ${userToken}`
                }
            }).then((response) => response.data.items)
            if (appointmentsData) {
                let headquartersIds: number[] = appointmentsData
                    .sort((a, b) => {
                        const nameA = a.office.parentOffice?.name || ''
                        const nameB = b.office.parentOffice?.name || ''
                        return nameA > nameB ? 1 : -1
                    })
                    .map((appData) => appData.office.parentOffice?.id || 0)
                headquartersIds = headquartersIds.filter((id, index) => headquartersIds.indexOf(id) === index)
                const appointmentsByHq = headquartersIds.map((hqId) =>
                    appointmentsData.filter((appData) => appData.office.parentOffice?.id === hqId && appData.active)
                )
                if (appointmentsByHq[0] && appointmentsByHq[0].length > 0) {
                    setUserAppointments(
                        appointmentsByHq.map((hqAppointments) => {
                            hqAppointments.sort((a, b) => (a.startsOn > b.startsOn ? 1 : -1))
                            return hqAppointments
                        })
                    )
                }
            }
        } catch (error) {
            console.error(error)
        }
    }, [userToken])

    /**
     * Se trova il parametro userToken nell'url richiede i dati degli appuntamenti
     */
    useEffect(() => {
        if (userToken) loadUserAppointmentsData()
    }, [userToken, loadUserAppointmentsData])

    /**
     * Cancella la prenotazione di un servizio
     * @param appointmentId id dell'appuntamento da annullare
     * @param eventId id dell'evento da eliminare
     * @param serviceId id del servizio correlato alla prenotazione
     */
    const removeAppointment = async (appointmentId: number) => {
        try {
            setLoading(true)
            await CustomAxios.delete(removeUserAppointment(appointmentId.toString()))
            if (userToken) await loadUserAppointmentsData()
        } catch (error) {
            console.error(error)
        }
        setLoading(false)
    }

    return (
        <>
            <div className="recap-data-container">
                <h2>Riepilogo appuntamenti</h2>
                {loading && <Loader />}
                {userAppointments && userInfo && (
                    <p className="subtitle">Gentile {userInfo.firstName}, di seguito il riepilogo delle prenotazioni effettuate.</p>
                )}
                {userAppointments && userAppointments.length > 0 ? (
                    userAppointments.map((appointmentsOfHq) => {
                        const hqName = appointmentsOfHq[0].office.parentOffice?.name || ''
                        const hqAddress = appointmentsOfHq[0].office.parentOffice?.address || ''
                        return (
                            <div key={appointmentsOfHq[0].id}>
                                <h3>{hqName}</h3>
                                <p>
                                    <FontAwesomeIcon className="appointment-data-wrapper__item__icon" icon={faMapMarker} size="lg" />
                                    <span>{hqAddress}</span>
                                </p>
                                {appointmentsOfHq.map((appointment) => {
                                    const appointmentDate = moment(appointment.startsOn)
                                    return (
                                        <div key={`${appointment.id}`} className="appointment-data-wrapper">
                                            <div className="subtitle appointment-data-wrapper__item">
                                                <div>
                                                    <FontAwesomeIcon className="appointment-data-wrapper__item__icon" icon={faSuitcase} size="lg" />
                                                </div>
                                                <div>
                                                    Servizio: <b>{appointment.service.name}</b>
                                                </div>
                                            </div>
                                            <div className="subtitle appointment-data-wrapper__item">
                                                <div>
                                                    <FontAwesomeIcon className="appointment-data-wrapper__item__icon" icon={faBuilding} size="lg" />
                                                </div>
                                                <div>Ufficio: {appointment.office.name}</div>
                                            </div>
                                            <div className="subtitle appointment-data-wrapper__item">
                                                <div>
                                                    <FontAwesomeIcon className="appointment-data-wrapper__item__icon" icon={faCalendar} size="lg" />
                                                </div>
                                                <div>
                                                    {appointmentDate.format('dddd D MMMM YYYY')} - Ore{' '}
                                                    {appointmentDate.format('HH:mm')}
                                                </div>
                                            </div>
                                            <div className="right-aligned-flex-row">
                                                <button className="primary-button" onClick={() => removeAppointment(appointment.id)}>
                                                    Annulla prenotazione
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })
                ) : (
                    <div>
                        <p>Non ci sono appuntamenti da mostrare in questa pagina.</p>
                        <Link to={ROUTES.HOMEPAGE_ROUTE} className="primary-button">
                            Prenota un servizio
                        </Link>
                    </div>
                )}
            </div>
            {/** <Footer /> */}
        </>
    )
}

export default AppointmentDetailPage
